<template>
  <div class="chart-card">
    <h3>Temettüler</h3>
    <div class="chart-container">
      <canvas ref="temettuChart"></canvas>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "TemettulerGrafik",
  props: {
    temettuler: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      chart: null,
      chartColors: {
        orange: "#F59F0BB3",
        blue: "#3b82f6b3",
      },
    };
  },
  mounted() {
    this.renderChart();
  },
  methods: {
    parseTarih(tarih) {
      // GG-AA-YYYY formatını parse ediyoruz
      const [gun, ay, yil] = tarih.split("-").map(Number);
      return new Date(yil, ay - 1, gun);
    },
    renderChart() {
      // Canvas elementini al
      const canvas = this.$refs.temettuChart;
      if (!canvas) return;

      // Önceki grafiği temizle
      if (this.chart) {
        this.chart.destroy();
        // Canvas'ı temizlemenin daha doğru yolu:
        const context = canvas.getContext("2d");
        if (context) {
          context.clearRect(0, 0, canvas.width, canvas.height);
        }
        this.chart = null;
      }

      // Geçerli veri kontrolü - null, undefined veya boş array ise çık
      if (!this.temettuler || this.temettuler.length === 0) {
        return;
      }

      // Canvas context'ini al
      const context = canvas.getContext("2d");
      if (!context) return;

      // Son 10 yılı hesapla
      const currentYear = new Date().getFullYear();
      const last10Years = Array.from(
        { length: 10 },
        (_, i) => currentYear - 9 + i
      ).sort((a, b) => a - b);

      // Her yıl için temettü verimlerini ve hisse başı net TL değerlerini topla
      const chartData = last10Years.map((year) => {
        const yilTemettuleri = this.temettuler.filter((temettu) => {
          // Tarihi manuel olarak parçalayıp yılı alalım (GG-AA-YYYY formatı)
          const tarihParcalari = temettu.Tarih?.split("-") || [];
          const temettuYear = parseInt(tarihParcalari[2], 10);

          // Geçerli bir temettü verimi var mı kontrol et
          const temettüVerim = temettu["Temettü Verim (%)"];
          const isValidYield =
            temettüVerim &&
            temettüVerim !== "Dağıtılmadı" &&
            !isNaN(parseFloat(temettüVerim.toString().replace(",", ".")));

          return temettuYear === year && isValidYield;
        });

        if (yilTemettuleri.length === 0) {
          // Bu yıl için veri yok
          return {
            year: year.toString(),
            verim: null, // null kullanıyoruz ki çizgi grafiği boşluk bıraksın
            netTL: null,
            hasData: false,
          };
        }

        // Temettü verimlerini topla (Türkçe formatı için virgülü noktaya çevir)
        const toplamVerim = yilTemettuleri.reduce((sum, temettu) => {
          const verimStr = temettu["Temettü Verim (%)"]?.toString() || "0";
          return sum + parseFloat(verimStr.replace(",", "."));
        }, 0);

        // Hisse başı net TL değerlerini topla
        const toplamNetTL = yilTemettuleri.reduce((sum, temettu) => {
          const netTLStr = temettu["Hisse Başı Net TL"]?.toString() || "0";
          return sum + parseFloat(netTLStr.replace(",", "."));
        }, 0);

        return {
          year: year.toString(),
          verim: parseFloat(toplamVerim.toFixed(2)),
          netTL: parseFloat(toplamNetTL.toFixed(4)),
          hasData: true,
        };
      });

      // Chart.js için veri hazırla
      this.chart = new Chart(context, {
        type: "bar",
        data: {
          labels: chartData.map((item) => item.year),
          datasets: [
            {
              label: "Hisse Başı Net TL",
              data: chartData.map((item) => item.netTL),
              backgroundColor: this.chartColors.blue,
              borderColor: this.chartColors.blue,
              borderWidth: 1,
              yAxisID: "y1",
              order: 2,
            },
            {
              label: "Temettü Verimi (%)",
              data: chartData.map((item) => item.verim),
              type: "line",
              backgroundColor: this.chartColors.orange,
              borderColor: this.chartColors.orange,
              borderWidth: 2,
              fill: false,
              pointRadius: 5,
              yAxisID: "y",
              order: 1,
              spanGaps: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              title: {
                display: true,
                text: "Yıl",
                color: "#f8fafc",
              },
              ticks: {
                color: "#f8fafc",
              },
              grid: {
                color: "#444",
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Temettü Verimi (%)",
                color: "#f8fafc",
              },
              ticks: {
                color: "#f8fafc",
              },
              grid: {
                color: "#444",
              },
              position: "left",
            },
            y1: {
              beginAtZero: true,
              position: "right",
              title: {
                display: true,
                text: "Hisse Başı Net TL",
                color: "#f8fafc",
              },
              ticks: {
                color: "#f8fafc",
                callback: function (value) {
                  return value.toFixed(2) + " TL";
                },
              },
              grid: {
                drawOnChartArea: false,
                color: "#444",
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  const datasetLabel = context.dataset.label;
                  const value = context.raw;

                  if (value === null) return `${datasetLabel}: Veri yok`;

                  if (datasetLabel === "Hisse Başı Net TL") {
                    return `${datasetLabel}: ${
                      value ? value.toFixed(4) : "0"
                    } TL`;
                  }
                  return `${datasetLabel}: %${value || "0"}`;
                },
              },
            },
            legend: {
              position: "top",
              labels: {
                color: "#f8fafc",
              },
            },
          },
        },
      });
    },
  },
  watch: {
    temettuler: {
      handler() {
        this.$nextTick(() => {
          this.renderChart();
        });
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.chart-card {
  padding: 1.5rem;
  background-color: var(--card-dark);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 350px;
  overflow: visible;
  margin-bottom: 20px;
}

.chart-card h3 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0;
}

.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
}

@media (max-width: 768px) {
  .chart-card {
    padding: 1rem 0.75rem;
    height: 330px;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .chart-card h3 {
    font-size: 14px;
    margin-bottom: 0;
  }

  .chart-container {
    height: 280px;
  }
}

@media (max-width: 480px) {
  .chart-card {
    padding: 0.75rem 0.5rem;
    height: 300px;
    gap: 0.5rem;
    margin-bottom: 2rem;
  }

  .chart-container {
    height: 250px;
  }
}
</style>
